import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaPiggyBank, FaChartLine, FaShieldAlt, FaStar, FaCheckCircle, FaFileAlt, FaUserCheck, FaHeart, FaChild, FaCalendarAlt, FaLock } from 'react-icons/fa';
import { MdAccountBalance, MdTrendingUp, MdAutorenew, MdElderly, MdHome } from 'react-icons/md';

const Deposits = () => {
    const { t } = useTranslation();

    const schemes = [
        {
            id: 'fixed',
            title: t('deposits.schemes.fixed.title'),
            subtitle: t('deposits.schemes.fixed.desc'),
            description: t('fixedDeposit.hero.description'),
            icon: <FaLock className="text-4xl text-white" />,
            isTable: true,
            items: t('deposits.schemes.fixed.slabs', { returnObjects: true }),
            bgGradient: "from-purple-500 to-purple-600"
        },
        {
            id: 'monthly',
            title: t('deposits.schemes.monthly.title'),
            subtitle: t('deposits.schemes.monthly.desc'),
            description: t('deposits.schemes.monthly.description'),
            icon: <FaChartLine className="text-4xl text-white" />,
            isTable: true,
            items: t('deposits.schemes.monthly.slabs', { returnObjects: true }),
            bgGradient: "from-blue-500 to-blue-600"
        },
        {
            id: 'pygmy',
            title: t('deposits.schemes.pygmy.title'),
            subtitle: t('deposits.schemes.pygmy.desc'),
            description: t('deposits.interestRates.pygmy.title'),
            icon: <MdAutorenew className="text-4xl text-white" />,
            isList: true,
            items: t('deposits.schemes.pygmy.rules', { returnObjects: true }),
            bgGradient: "from-emerald-500 to-emerald-600"
        },
        {
            id: 'setting',
            title: t('deposits.schemes.setting.title'),
            subtitle: t('deposits.schemes.setting.desc'),
            description: t('savingAccount.hero.description'),
            icon: <FaPiggyBank className="text-4xl text-white" />,
            isRate: true,
            rate: t('deposits.schemes.setting.rate'),
            bgGradient: "from-blue-500 to-blue-600"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
            {/* 1. Hero Section - Corporate Style */}
            <section className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-4 block">{t('deposits.badge')}</span>
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t('deposits.title')}</h1>
                    <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                        {t('deposits.subtitle')}
                    </p>
                </div>
            </section>

            {/* 2. Intro Benefits Grid */}
            <section className="py-16 bg-white relative -mt-10 z-20 mx-4 md:mx-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-secondary text-center">
                            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary text-3xl mx-auto mb-4">
                                <FaShieldAlt />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('deposits.benefits.secure')}</h3>
                            <p className="text-gray-600">{t('deposits.benefits.secureDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-secondary text-center">
                            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary text-3xl mx-auto mb-4">
                                <MdTrendingUp />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('deposits.benefits.highInterest')}</h3>
                            <p className="text-gray-600">{t('deposits.benefits.highInterestDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-secondary text-center">
                            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary text-3xl mx-auto mb-4">
                                <MdAccountBalance />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('deposits.benefits.flexible')}</h3>
                            <p className="text-gray-600">{t('deposits.benefits.flexibleDesc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. Detailed Schemes Sections */}
            <div className="space-y-20 py-20">
                {schemes.map((scheme, index) => (
                    <section key={scheme.id} id={scheme.id} className={`scroll-mt-24 py-16 md:py-24 ${index % 2 !== 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className={`flex flex-col ${index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-12 items-center`}>
                                {/* Content Side */}
                                <div className="md:w-1/2">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{scheme.title}</h2>
                                    <p className="text-lg text-primary font-medium mb-4">{scheme.subtitle}</p>
                                    <p className="text-gray-600 leading-relaxed mb-8">{scheme.description}</p>

                                    <Link
                                        to={`/contact?service=${encodeURIComponent(scheme.title)}`}
                                        className="inline-block px-8 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition-colors duration-300 shadow-md hover:shadow-lg"
                                    >
                                        {t('deposits.applyNow')}
                                    </Link>
                                </div>

                                {/* Data Display Side */}
                                <div className="md:w-1/2 w-full">
                                    {scheme.isTable && (
                                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-primary text-white">
                                                        <th className="px-6 py-4 font-bold uppercase tracking-wider text-sm">{t('deposits.interestRates.period')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {(scheme.items || []).map((item, idx) => (
                                                        <tr key={idx} className="hover:bg-blue-50 transition-colors">
                                                            <td className="px-6 py-4 text-gray-700 font-medium">{item}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {scheme.isList && (
                                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 border-l-8 border-primary">
                                            <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                                {t('deposits.interestRates.pygmy.title')}
                                            </h4>
                                            <ul className="space-y-4">
                                                {(scheme.items || []).map((item, idx) => (
                                                    <li key={idx} className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl text-primary font-bold text-lg">
                                                        <FaCheckCircle className="text-primary" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {scheme.isRate && (
                                        <div className="bg-white rounded-2xl shadow-xl p-10 border border-gray-100 border-l-8 border-primary flex flex-col items-center text-center">
                                            <h4 className="text-2xl font-bold text-gray-900 mb-4">{scheme.title}</h4>
                                            <div className="px-8 py-4 bg-primary text-white rounded-full font-bold text-3xl shadow-lg ring-4 ring-primary/20">
                                                {scheme.rate}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            {/* 4. Common Documentation & Eligibility */}
            <section className="py-20 bg-primary text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                        {/* Eligibility */}
                        <div>
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                                    <FaUserCheck className="text-2xl text-secondary" />
                                </div>
                                <h2 className="text-3xl font-bold">{t('deposits.eligibility.heading')}</h2>
                            </div>
                            <div className="space-y-6">
                                <div className="bg-white/5 backdrop-blur-md p-6 rounded-xl border border-white/10">
                                    <h3 className="font-bold text-xl mb-2 text-secondary">{t('deposits.eligibility.whoCanApply')}</h3>
                                    <p className="text-blue-100">{t('deposits.eligibility.desc')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Documents */}
                        <div>
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                                    <FaFileAlt className="text-2xl text-secondary" />
                                </div>
                                <h2 className="text-3xl font-bold">{t('deposits.documentation.heading')}</h2>
                            </div>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    t('savingAccount.documents.doc1'),
                                    t('savingAccount.documents.doc2'),
                                    t('savingAccount.documents.doc3'),
                                    t('savingAccount.documents.doc4'),
                                    t('savingAccount.documents.doc5')
                                ].map((doc, idx) => (
                                    <li key={idx} className="flex items-center gap-3 bg-white/5 p-4 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                                        <FaCheckCircle className="text-secondary flex-shrink-0" />
                                        <span className="text-sm text-blue-50">{doc}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Deposits;
